import enMessages from 'locales/en.json';

import * as cookies from 'lib/cookies';

export const DEFAULT_LOCALE = 'en';
export const SUPPORTED_LOCALES = [ 'en', 'zh', 'es', 'ja', 'ko' ] as const;
// Reuse the canonical cookie name from `lib/cookies` so the two definitions cannot drift.
export const LOCALE_COOKIE_NAME = cookies.NAMES.LOCALE;
export const LOCALE_STORAGE_KEY = 'locale';

export type Locale = typeof SUPPORTED_LOCALES[number];
export type Messages = typeof enMessages;
export type MessageParams = Record<string, string | number | null | undefined>;

export const LOCALE_LABELS: Record<Locale, string> = {
  en: 'English',
  zh: '中文',
  es: 'Español',
  ja: '日本語',
  ko: '한국어',
};

type MessagesModule = { 'default': Messages };

const MESSAGE_LOADERS: Record<Locale, () => Promise<MessagesModule>> = {
  en: () => Promise.resolve({ 'default': enMessages }),
  zh: () => import('locales/zh.json'),
  es: () => import('locales/es.json'),
  ja: () => import('locales/ja.json'),
  ko: () => import('locales/ko.json'),
};

const MESSAGES: Partial<Record<Locale, Messages>> = {
  en: enMessages,
};

let loadedMessagesLocale: Locale = DEFAULT_LOCALE;

const PLACEHOLDER_RE = /\{\{\s*([^{}\s]+)\s*\}\}/g;

export function isSupportedLocale(value: unknown): value is Locale {
  return typeof value === 'string' && SUPPORTED_LOCALES.includes(value as Locale);
}

export function normalizeLocale(value: unknown): Locale {
  return isSupportedLocale(value) ? value : DEFAULT_LOCALE;
}

export function getLocaleFromCookie(cookieString?: string): Locale {
  if (!cookieString) {
    return DEFAULT_LOCALE;
  }

  const cookie = cookieString
    .split(';')
    .map((item) => item.trim())
    .find((item) => item.startsWith(`${ LOCALE_COOKIE_NAME }=`));

  if (!cookie) {
    return DEFAULT_LOCALE;
  }

  const value = decodeURIComponent(cookie.slice(LOCALE_COOKIE_NAME.length + 1));
  return normalizeLocale(value);
}

export function getLoadedMessagesLocale() {
  return loadedMessagesLocale;
}

export function hasLocaleMessages(locale: Locale) {
  return Boolean(MESSAGES[locale]);
}

export function setLocaleMessages(locale: Locale, messages: Messages) {
  MESSAGES[locale] = messages;
  loadedMessagesLocale = locale;
}

export async function loadMessages(locale: Locale) {
  const existingMessages = MESSAGES[locale];

  if (existingMessages) {
    loadedMessagesLocale = locale;
    return existingMessages;
  }

  const messagesModule = await MESSAGE_LOADERS[locale]();
  setLocaleMessages(locale, messagesModule.default);

  return messagesModule.default;
}

export function getMessages(locale: Locale): Messages {
  return MESSAGES[locale] ?? MESSAGES[DEFAULT_LOCALE]!;
}

export function getMessage(messages: unknown, key: string): string | undefined {
  const value = key.split('.').reduce<unknown>((result, pathItem) => {
    if (!result || typeof result !== 'object' || Array.isArray(result)) {
      return undefined;
    }

    return (result as Record<string, unknown>)[pathItem];
  }, messages);

  return typeof value === 'string' ? value : undefined;
}

export function interpolateMessage(message: string, params: MessageParams = {}): string {
  return message.replace(PLACEHOLDER_RE, (placeholder, name: string) => {
    const value = params[name];
    return value === null || value === undefined ? placeholder : String(value);
  });
}

// Track keys we've already reported so a missing key inside a render loop
// doesn't flood the console (or Rollbar, if it captures console.warn).
const reportedMissingKeys = new Set<string>();

function reportMissingKey(locale: Locale, key: string) {
  if (reportedMissingKeys.has(key)) {
    return;
  }
  reportedMissingKeys.add(key);

  // eslint-disable-next-line no-restricted-properties
  if (process.env.NODE_ENV === 'test') {
    return;
  }

  // eslint-disable-next-line no-console
  console.warn(`[i18n] missing translation for key "${ key }" (locale=${ locale }, default=${ DEFAULT_LOCALE })`);
}

export function translateFromMessages(messages: Messages, locale: Locale, key: string, params?: MessageParams): string {
  const message = getMessage(messages, key) ?? getMessage(getMessages(DEFAULT_LOCALE), key);

  if (message === undefined) {
    reportMissingKey(locale, key);
    return key;
  }

  return interpolateMessage(message, params);
}

export function translate(locale: Locale, key: string, params?: MessageParams): string {
  return translateFromMessages(getMessages(locale), locale, key, params);
}
